import { CopilotHooks, HookObject, ListItemsHooks } from "@/plugins/types";
import { ChatBot } from "./ChatBot";
import { BODY_ID, ComponentTypes } from "../Pages/Editor/types";
import { Sparkle, Textbox } from "@phosphor-icons/react";
import { CopilotGallery } from "./CopilotGallery";
import { Copilot } from "./Copilot";
import { SettingsIcon, Sparkles, Image } from "lucide-react";
import { handleAskAIComponentRequest, handleGetAlternateCopyRequest } from "../Pages/Dashboard";
import artifacts from "./prompts/artifacts";
import general from "./prompts/general";
import { listAncestors, listChildren, recursivelyRetrieveItem } from "../Pages/V2Editor/actions";
import { isJsonString } from "@/utils";
import { getBasicUserInfo } from "@/api/auth";
import { convertPagetoHTML } from "../Pages/utils";
import { addBodyIfNone } from "./Artifacts/PageBuilderArtifact";
import html2canvas from "html2canvas";
import { ObjectTaggingModal } from "./components/ObjectTaggingModal";
import { useState } from "react";
import ReactJson from "react-json-view";
import { AI_DOMAIN } from "@launchos/shared-utils/env";
// import { useCmdK } from "@/ui/CommunicationView/CmdK/CmdK";

// Helper function to convert images to data URLs to avoid CORS issues

const EditComponentSettings = (props) => {
  const [newSettings, setNewSettings] = useState(props.settings);
  const { content, html, settings, updateEditorComponent, hideModal } = props;

  const onClose = () => {
    hideModal();
  };

  const onSuccess = () => {
    updateEditorComponent(settings.id, newSettings);
    hideModal();
  };

  return (
    <div className="">
      <style>
        {`
          /* Make ReactJson text fields wider during edit mode */
          .react-json-view input[type="text"],
          .react-json-view input[type="number"],
          .react-json-view textarea {
            min-width: 200px !important;
            width: 750px !important;
          }
        `}
      </style>
      <div className="h-full overflow-y-auto bg-white max-h-[36rem] p-4 rounded-lg">
        <ReactJson
          src={newSettings}
          name="settings"
          collapsed={1}
          onEdit={(options) => {
            setNewSettings(options.updated_src);
          }}
          onAdd={(options) => {
            setNewSettings(options.updated_src);
          }}
          onDelete={(options) => {
            setNewSettings(options.updated_src);
          }}
        />
      </div>
      <div className="flex pt-4 gap-4">
        <button
          type="button"
          className="btn bg-black/10 text-black/70"
          onClick={onClose}
        >
          Cancel
        </button>
        <button type="button" className="btn" onClick={onSuccess} title="Save">
          OK
        </button>
      </div>
    </div>
  );
};



const convertImagesToDataUrls = async (htmlContent: string): Promise<string> => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const images = doc.querySelectorAll('img');
  
  const imagePromises = Array.from(images).map(async (img) => {
    const src = img.getAttribute('src');
    if (src && (src.startsWith('http://') || src.startsWith('https://'))) {
      try {
        // Try different approaches to get the image
        let imageBlob: Blob | null = null;
        
        // Method 1: Try direct fetch with CORS
        try {
          const response = await fetch(src, { mode: 'cors' });
          if (response.ok) {
            imageBlob = await response.blob();
          }
        } catch (e) {
          console.log('Direct fetch failed, trying proxy...');
        }
        
        // Method 2: Try proxy if direct fetch failed
        if (!imageBlob) {
          try {
            const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(src)}`;
            const proxyResponse = await fetch(proxyUrl);
            if (proxyResponse.ok) {
              imageBlob = await proxyResponse.blob();
            }
          } catch (e) {
            console.log('Proxy failed, trying alternative...');
          }
        }
        
        // Method 3: Try alternative proxy
        if (!imageBlob) {
          try {
            const proxyUrl2 = `https://cors-anywhere.herokuapp.com/${src}`;
            const proxyResponse2 = await fetch(proxyUrl2);
            if (proxyResponse2.ok) {
              imageBlob = await proxyResponse2.blob();
            }
          } catch (e) {
            console.log('Alternative proxy failed');
          }
        }
        
        // Method 4: Try with no-cors mode
        if (!imageBlob) {
          try {
            const response = await fetch(src, { mode: 'no-cors' });
            if (response.type === 'opaque') {
              // For no-cors, we can't access the content, so we'll remove the image
              console.warn('No-cors mode not sufficient for image:', src);
              img.remove();
              return;
            }
          } catch (e) {
            console.log('No-cors fetch failed');
          }
        }
        
        if (imageBlob) {
          // Convert blob to data URL
          const reader = new FileReader();
          const dataUrlPromise = new Promise<string>((resolve) => {
            reader.onload = () => resolve(reader.result as string);
          });
          reader.readAsDataURL(imageBlob);
          const dataUrl = await dataUrlPromise;
          img.setAttribute('src', dataUrl);
          console.log('Successfully converted image to data URL:', src);
        } else {
          // If all methods fail, remove the image
          console.warn('All methods failed for image:', src);
          img.remove();
        }
      } catch (error) {
        console.warn('Failed to process image:', src, error);
        img.remove();
      }
    }
  });
  
  await Promise.all(imagePromises);
  return doc.documentElement.outerHTML;
};

// Helper function to handle CSS background images
const handleBackgroundImages = (htmlContent: string): string => {
  // Remove external background images from CSS to avoid CORS issues
  return htmlContent.replace(
    /background-image:\s*url\(['"]?([^'"]+)['"]?\)/gi,
    (match, url) => {
      if (url.startsWith('http://') || url.startsWith('https://')) {
        console.warn('Removing external background image:', url);
        return 'background-image: none;';
      }
      return match;
    }
  );
};

const updateComponentSettingsMenuItem = [
  { id: "divider" },
  // { id: "update-settings", iconComponent: <SettingsIcon />, text: "Edit Component Settings" },
  { id: "generate-description", iconComponent: <Textbox className="text-lg" />, text: "Set Group Description" },
  // { id: "generate-image", iconComponent: <Image className="text-lg" />, text: "Convert to Image" },
];

export const onListItems = (
  { id, position, type },
  payload,
  actions,
  // { permissions = [] }
) => {
  const user = getBasicUserInfo()
  // console.log("user details", user, "is admin", user?.permissions?.includes("admin"));
  // const { permissions = [] } = user;
  const permissions = user?.permissions || [];

  if (id === ListItemsHooks.EDITOR_OBJECT_MENU) {

    // alert(JSON.stringify(permissions))

    if (type === ComponentTypes.TEXT || type === ComponentTypes.PARAGRAPH || type === ComponentTypes.HEADLINE) {
      return [
        { id: "ai-ask", iconComponent: <Textbox className="text-lg" />, text: "Ask Copilot" },
        { id: "ai-copy",  iconComponent: <Sparkles className="scale-75 -ml-1" />, text: "Improve Copy" },
        // { id: "prompt", iconComponent: <></>, text: "Set Prompt" },
        { id: "divider" },
        ...payload,
        ...(permissions.includes("admin") ? updateComponentSettingsMenuItem : []),
      ];
    }

    return [
      { id: "ai-ask", iconComponent: <Textbox className="text-lg" />, text: "Ask Copilot" },
      // { id: "prompt", iconComponent: <></>, text: "Set Prompt" },
      { id: "divider" },
      ...payload,
      ...(permissions.includes("admin") ? updateComponentSettingsMenuItem : []),
    ];

  }

  if (id === ListItemsHooks.APP_DRAWER && position === "top") {
    if (!permissions.includes("copilot")) return [];
    // return [
    //   {
    //     icon: <Sparkle className="text-lg" />,
    //     label: "Copilot",
    //     href: "/copilot",
    //   },
    // ];
  }

  if (id === ListItemsHooks.ROUTES) {
    return [
      {
        label: "Copilot",
        path: "/copilot",
        element: <Copilot name="Launch Copilot" />,
        // element: <div>Copilot</div>,
        children: [
          {
            path: ":id",
            element: <Copilot name="Launch Copilot - Name" />,
          },
          {
            path: ":id/replay",
            element: <Copilot name="Launch Copilot - Replay" sessionReplay={true} />,
          },
        ],
      },
      // {
      //   label: "Copilot",
      //   path: "/copilot/all",
      //   element: <CopilotGallery name="Launch Copilot" />,
      // },
      // {
      //   label: "Copilot",
      //   path: "/copilot/test",
      //   element: <ChatBot />,
      // },
    ];
  }
};

export const onChatPrompt = (
  hook: HookObject,
  { context },
  { copilotContext }
) => {
  if (hook.id === CopilotHooks.GET_SYSTEM_PROMPT && context && copilotContext) {
    // const { copilotContext } = actions;
    console.log("About to System Prompt", context, copilotContext);

    let systemPrompt = "";
    
    // grab general and artifact prompt
    // systemPrompt += artifacts;
    // systemPrompt += '\n\n';
    // systemPrompt += general;

    // console.log("The System Prompt", systemPrompt);

    return systemPrompt;
  }
};

export const onMenuSelect = async ({ id, type }, payload, { openCmdK, complete, openModal, hideModal }) => {
  console.log("onMenuSelect", id, type, payload, { openCmdK, complete, openModal });
  if (id === ListItemsHooks.EDITOR_OBJECT_MENU && payload.id === "ai-ask") {
    const { settings, updateComponentSettings, pageContent } = payload;

    const ancestors = listAncestors(pageContent, settings.id);
    const section = ancestors?.find(({ type }) => type === ComponentTypes.SECTION)
    const sectionChildrenOne = listChildren(pageContent, section.id);
    const sectionChildrenTwo = recursivelyRetrieveItem(pageContent, section.id);    
 
    console.log("Current Payload Is...", payload, ancestors, { sectionChildrenOne, sectionChildrenTwo});

    const prompt = window.prompt("Page Instructions...[esc] to close");
    if (!prompt) return;
    // openCmdK();
    if (type === ComponentTypes.TEXT || type === ComponentTypes.PARAGRAPH || type === ComponentTypes.HEADLINE) {

      await handleGetAlternateCopyRequest({
        settings,
        prompt,
        updateComponent: updateComponentSettings,
      });

    }    
    else if (type === ComponentTypes.IMAGE || type === ComponentTypes.PICTURE) {
      // prompt the user for a new image
      // 1. Send the image to the AI API to generate a new image
      const newImage = await fetch(`${AI_DOMAIN}/api/image`, {
        method: "POST",
        body: JSON.stringify({ prompt }),
      });
      const newImageData = await newImage.json();
      // const newImage = newImageData.images[0].data;
      // 2. Replace the image with the new image
      console.log("New Image", newImageData);
      // 3. Update the component settings with the new image
      if (!newImageData) return;
      updateComponentSettings(settings.id, {...settings, src: `data:image/png;base64,${newImageData.images[0].data}`});
    }
    else {
      await handleAskAIComponentRequest({ 
        prompt,
        payload,        
      });
    }
  } else if (id === ListItemsHooks.EDITOR_OBJECT_MENU && payload?.id === "ai-copy") {
    if (type === ComponentTypes.TEXT || type === ComponentTypes.PARAGRAPH || type === ComponentTypes.HEADLINE) {
      const { settings, updateComponentSettings } = payload;
      await handleGetAlternateCopyRequest({
        settings,
        updateComponent: updateComponentSettings,
      });      
    }
  } else if (id === ListItemsHooks.EDITOR_OBJECT_MENU && payload?.id === "rename") {
    const { updateComponentSettings, settings } = payload;
    // const newSettings = window.prompt("Update Settings...[esc] to close", JSON.stringify(settings));
    // if (!newSettings) return;
    // if (!isJsonString(newSettings)) {
    //   alert("Invalid Settings, try again!");
    //   return;
    // } else {
    //   updateComponentSettings(settings.id, JSON.parse(newSettings));
    // }

    // showModal
    openModal({
      title: `Edit ${settings.type} Settings`,
      payload: {
        settings,
      },
      component: EditComponentSettings,
    });
  } else if (id === ListItemsHooks.EDITOR_OBJECT_MENU && payload?.id === "prompt") {
    const { updateComponentSettings, settings } = payload;
    const prompt = window.prompt("Update Prompt...[esc] to close", settings?.prompt);
    if (!prompt) return;
    updateComponentSettings(settings.id, {...settings, prompt});
  } else if (id === ListItemsHooks.EDITOR_OBJECT_MENU && payload?.id === "generate-description") {
    const { settings, updateComponentSettings, pageContent } = payload;
    // console.log("Generate Description", pageContent, settings);
    const itemContent = recursivelyRetrieveItem(pageContent, settings.id);
    const itemHTML = convertPagetoHTML(addBodyIfNone(itemContent.map((itm) => {
      if (itm.id === settings.id) return { ...itm, parent: BODY_ID };
      return itm;
    })));

    // showModal
    openModal({
      title: `Tag This ${settings.type}`,
      payload: { 
        content: itemContent,
        html: itemHTML,
        settings,
      },
      component: ObjectTaggingModal,
    });

    // console.log("Generate Description", itemContent, itemHTML);
  } /* else if (id === ListItemsHooks.EDITOR_OBJECT_MENU && payload?.id === "generate-image") {
    const { settings, updateComponentSettings, pageContent } = payload;
    const itemContent = recursivelyRetrieveItem(pageContent, settings.id);
    const itemHTML = convertPagetoHTML(addBodyIfNone(itemContent.map((itm) => {
      if (itm.id === settings.id) return { ...itm, parent: BODY_ID };
      return itm;
    })));
    
    console.log("Generate Image", itemContent, itemHTML);
    
    try {
      // Handle CSS background images first
      const htmlWithoutBgImages = handleBackgroundImages(itemHTML);
      
      // Convert all external images to data URLs to avoid CORS issues
      const processedHTML = await convertImagesToDataUrls(htmlWithoutBgImages);
      
      // Create a temporary div to render the HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = processedHTML;
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.top = '-9999px';
      tempDiv.style.width = '100%';
      tempDiv.style.height = 'auto';
      document.body.appendChild(tempDiv);
      
      // Wait a bit for images to load
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Convert to canvas and then to data URL
      html2canvas(tempDiv, {
        allowTaint: false, // Set to false since we're using data URLs
        useCORS: false, // Set to false since we're using data URLs
        scale: 2, // Higher quality
        backgroundColor: '#ffffff',
        logging: true, // Enable logging for debugging
        ignoreElements: (element) => {
          // Ignore any remaining external images that couldn't be converted
          if (element.tagName === 'IMG') {
            const src = element.getAttribute('src');
            if (src && (src.startsWith('http://') || src.startsWith('https://'))) {
              console.warn('Ignoring external image that couldn\'t be converted:', src);
              return true; // Ignore this element
            }
          }
          return false;
        },
        onclone: (clonedDoc) => {
          // Additional processing on the cloned document if needed
          console.log('Cloned document for canvas generation');
          
          // Also handle CSS background images in the cloned document
          const elementsWithBg = clonedDoc.querySelectorAll('*');
          elementsWithBg.forEach((element) => {
            const style = window.getComputedStyle(element);
            const backgroundImage = style.backgroundImage;
            if (backgroundImage && backgroundImage !== 'none') {
              const urlMatch = backgroundImage.match(/url\(['"]?([^'"]+)['"]?\)/);
              if (urlMatch) {
                const bgUrl = urlMatch[1];
                if (bgUrl.startsWith('http://') || bgUrl.startsWith('https://')) {
                  console.warn('External background image detected:', bgUrl);
                  // Remove the background image to avoid CORS issues
                  (element as HTMLElement).style.backgroundImage = 'none';
                }
              }
            }
          });
        }
      }).then((canvas) => {
        const dataUrl = canvas.toDataURL('image/png');
        console.log("Generated image data URL:", dataUrl);
        
        // Clean up the temporary div
        document.body.removeChild(tempDiv);
        
        // You can download the image or use it as needed
        const link = document.createElement('a');
        link.download = `component-${settings.id}.png`;
        link.href = dataUrl;
        link.click();
      }).catch((error) => {
        console.error("Error generating image:", error);
        document.body.removeChild(tempDiv);
      });
    } catch (error) {
      console.error("Error processing images:", error);
    }
  }*/ 
};

export { ChatBot };
